/* © ИП Коробкин И.Н., KIN DIGITAL CRM+ */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FormEvent, useState } from 'react';
import { EXPENSE_KIND_LABEL } from '@kd/shared';
import { Badge, Button, Empty, ErrorText, Field, Input, Modal, PageHeader, Pager, Select, Spinner, Tabs } from '@/components/ui';
import { api, qs } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { fmtDate, fmtMoney, today } from '@/lib/format';
import { useDict } from '@/lib/hooks';

type K = 'PRODUCTION' | 'OVERHEAD';
const ST: Record<string, { l: string; t: 'slate' | 'green' | 'red' }> = { DRAFT: { l: 'на согласовании', t: 'slate' }, APPROVED: { l: 'согласован', t: 'green' }, REJECTED: { l: 'отклонён', t: 'red' } };

export function ExpensesPage() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const { data: cats } = useDict('expense_category');
  const catName = (c: string) => cats?.find((x) => x.code === c)?.name ?? c;
  const [kind, setKind] = useState<K>('PRODUCTION');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ['expenses', kind, status, page], queryFn: () => api.get(`/expenses${qs({ kind, status, page, pageSize: 50 })}`) });
  const decide = useMutation({ mutationFn: (b: { id: string; approve: boolean }) => api.post(`/expenses/${b.id}/decision`, { approve: b.approve }), onSuccess: () => qc.invalidateQueries({ queryKey: ['expenses'] }) });
  const billed = useMutation({ mutationFn: (id: string) => api.post(`/expenses/${id}/billed`), onSuccess: () => qc.invalidateQueries({ queryKey: ['expenses'] }) });
  return (
    <>
      <PageHeader title="Затраты и расходы" actions={can('expense.write') && <Button onClick={() => setCreating(true)}>Добавить</Button>} />
      <Tabs<K> value={kind} onChange={(k) => { setKind(k); setPage(1); }} tabs={[{ id: 'PRODUCTION', label: EXPENSE_KIND_LABEL.PRODUCTION }, { id: 'OVERHEAD', label: EXPENSE_KIND_LABEL.OVERHEAD }]} />
      <div className="card">
        <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 p-3">
          <Select className="max-w-[200px]" value={status} onChange={(e) => setStatus(e.target.value)} placeholder="Все" options={Object.entries(ST).map(([value, s]) => ({ value, label: s.l }))} />
          <div className="text-sm">Итого: <b className="tabular-nums">{fmtMoney(data?.sum ?? 0)}</b></div>
        </div>
        {isLoading ? <Spinner /> : !data?.items.length ? <Empty>Записей нет</Empty> : (
          <div className="overflow-x-auto"><table className="w-full">
            <thead className="bg-slate-50"><tr><th className="th">Дата</th><th className="th">Статья</th><th className="th">Описание</th><th className="th text-right">Сумма</th><th className="th">Статус</th><th className="th"></th></tr></thead>
            <tbody>{data.items.map((e: any) => (
              <tr key={e.id} className="border-t border-slate-100">
                <td className="td">{fmtDate(e.date)}</td><td className="td">{catName(e.category)}</td>
                <td className="td">{e.description}{e.reimbursable && <Badge tone="blue">к возмещению{e.billedAt ? ', выставлен' : ''}</Badge>}</td>
                <td className="td text-right tabular-nums">{fmtMoney(e.amount)}</td>
                <td className="td"><Badge tone={ST[e.status].t}>{ST[e.status].l}</Badge></td>
                <td className="td whitespace-nowrap text-right">
                  {can('expense.approve') && e.status === 'DRAFT' && <>
                    <Button size="sm" variant="secondary" onClick={() => decide.mutate({ id: e.id, approve: true })}>Согласовать</Button>{' '}
                    <Button size="sm" variant="ghost" onClick={() => decide.mutate({ id: e.id, approve: false })}>Отклонить</Button>
                  </>}
                  {can('expense.approve') && e.status === 'APPROVED' && e.reimbursable && !e.billedAt && <Button size="sm" variant="secondary" onClick={() => billed.mutate(e.id)}>Выставлен клиенту</Button>}
                </td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
        {data && <Pager page={page} pageSize={50} total={data.total} onPage={setPage} />}
      </div>
      <Modal title="Новая запись" open={creating} onClose={() => setCreating(false)}>
        <ExpenseForm kind={kind} onSaved={() => { setCreating(false); qc.invalidateQueries({ queryKey: ['expenses'] }); }} />
      </Modal>
    </>
  );
}

function ExpenseForm({ kind, onSaved }: { kind: K; onSaved: () => void }) {
  const { data: cats } = useDict('expense_category');
  const { data: matters } = useQuery({ queryKey: ['matters-pick'], queryFn: () => api.get('/matters?status=OPEN&pageSize=200') });
  const { data: contracts } = useQuery({ queryKey: ['contracts-pick'], queryFn: () => api.get('/contracts?status=ACTIVE&pageSize=200') });
  const [f, setF] = useState({ kind, category: '', amount: '', date: today(), description: '', matterId: '', contractId: '', reimbursable: false });
  const save = useMutation({
    mutationFn: () => api.post('/expenses', { ...f, matterId: f.matterId || null, contractId: f.contractId || null, description: f.description || null }),
    onSuccess: onSaved,
  });
  const submit = (e: FormEvent) => { e.preventDefault(); save.mutate(); };
  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Вид"><Select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value as K })} options={Object.entries(EXPENSE_KIND_LABEL).map(([value, label]) => ({ value, label }))} /></Field>
        <Field label="Статья"><Select required value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} placeholder="Выберите" options={(cats ?? []).map((c) => ({ value: c.code, label: c.name }))} /></Field>
        <Field label="Сумма, ₽"><Input required inputMode="decimal" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value.replace(',', '.') })} /></Field>
        <Field label="Дата"><Input type="date" required value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></Field>
      </div>
      <Field label="Описание"><Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
      <Field label="Дело"><Select value={f.matterId} onChange={(e) => setF({ ...f, matterId: e.target.value })} placeholder="Без дела" options={(matters?.items ?? []).map((m: any) => ({ value: m.id, label: `№ ${m.number} ${m.title}` }))} /></Field>
      <Field label="Договор"><Select value={f.contractId} onChange={(e) => setF({ ...f, contractId: e.target.value })} placeholder="Без договора" options={(contracts?.items ?? []).map((c: any) => ({ value: c.id, label: `№ ${c.number} - ${c.partyName}` }))} /></Field>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.reimbursable} onChange={(e) => setF({ ...f, reimbursable: e.target.checked })} /> Подлежит возмещению клиентом / за счёт процедуры</label>
      <ErrorText error={save.error} />
      <div className="flex justify-end"><Button type="submit" disabled={save.isPending}>Сохранить</Button></div>
    </form>
  );
}
